import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import { ToastContext, type ToastApi } from './toast-context';

/**
 * Transient confirmation, mounted once at the app root.
 *
 * v1 had every screen render its own toast inline, then corrected the
 * resulting layout shift with a global `[class$="__toast"] { position: fixed
 * !important }` line in index.css. That hack is the single clearest artefact
 * of what happens without shared primitives — it had to reach into other
 * components' class names to work at all.
 *
 * Copy line: a toast reports the action in the past tense using the same verb
 * as the control that caused it. "Publish" produces "Published", never
 * "Success!". A toast that says "Success" tells the user nothing.
 */

type ToastTone = 'ok' | 'due' | 'info';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
  /** Detail line — an error's cause, or the record affected. */
  detail?: string;
}

/**
 * ── Why the toast is built from two elements ──
 * The tone fills (`bg-success-soft` and friends) are 8–10% alpha. That is the
 * right value for a <Badge>, which always sits on an opaque white surface — but
 * a toast is `position: fixed` over whatever the page happens to be showing, so
 * the same fill let a table's rows read straight through the message.
 *
 * So the card itself is opaque `bg-surface` and the tone wash is a layer inside
 * it. The tint composites over white, as it was always designed to, and the
 * text sits on a known background instead of on a register.
 */
const tones: Record<
  ToastTone,
  { fill: string; rule: string; accent: string; icon: 'circleCheck' | 'alert' | 'info' }
> = {
  ok: {
    fill: 'bg-success-soft',
    rule: 'border-l-success',
    accent: 'text-success',
    icon: 'circleCheck',
  },
  due: { fill: 'bg-danger-soft', rule: 'border-l-danger', accent: 'text-danger', icon: 'alert' },
  info: { fill: 'bg-info-soft', rule: 'border-l-info', accent: 'text-info', icon: 'info' },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const push = useCallback((tone: ToastTone, message: string, detail?: string) => {
    const id = nextId.current++;
    setItems((prev) => [...prev, { id, tone, message, detail }]);
    // Errors stay longer — they carry instructions the user has to read.
    const ttl = tone === 'due' ? 7000 : 4000;
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), ttl);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      ok: (m, d) => push('ok', m, d),
      error: (m, d) => push('due', m, d),
      info: (m, d) => push('info', m, d),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed right-4 top-chrome z-60 mt-2 flex w-80 flex-col gap-2"
          role="status"
          aria-live="polite"
          data-print="hide"
        >
          {items.map((t) => (
            <div
              key={t.id}
              className="pointer-events-auto overflow-hidden rounded-md border border-line bg-surface shadow-dropdown"
            >
              {/* The tone lives here — a wash over the opaque card and a rule
                  down the leading edge, the same left-rule idiom the register
                  uses to flag a row. */}
              <div
                className={cx(
                  'flex items-start gap-2 border-l-2 px-2.5 py-2',
                  tones[t.tone].fill,
                  tones[t.tone].rule,
                )}
              >
                <Icon
                  name={tones[t.tone].icon}
                  size={15}
                  strokeWidth={2}
                  className={cx('mt-0.5', tones[t.tone].accent)}
                />
                <div className="min-w-0 flex-1">
                  {/* Ink, not the tone colour. Coloured body text on a coloured
                      wash was the other half of why this was hard to read. */}
                  <p className="text-sm font-semibold text-ink">{t.message}</p>
                  {t.detail && <p className="mt-0.5 text-xs text-muted">{t.detail}</p>}
                </div>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => setItems((prev) => prev.filter((x) => x.id !== t.id))}
                  className="mt-0.5 shrink-0 text-faint transition-colors hover:text-ink"
                >
                  <Icon name="close" size={13} strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
